// src/client/components/profile-manager/detail-view/ProfileDetailPage.tsx
// ✅ FIXED: Proper refetch after category changes

import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiClient } from '@client/lib/api-client';
import type { ProfileWithRelations } from '@shared/types';

import ProfileDetailHeader from './ProfileDetailHeader';
import BasicInformationSection from './BasicInformationSection';
import LocationSection from './LocationSection';
import ProfileDataSection from './ProfileDataSection';
import RelationsSection from './RelationsSection';
import StatusMarkersSection from './StatusMarkersSection';
import TechnicalDataSection from './TechnicalDataSection';
import ProfileImageAndData from './ProfileImageAndData';

import {
  useStaticDataQueries,
} from "@client/hooks/useProfilesQuery";

import { useProfileNavigation } from '@client/hooks/useProfileNavigation';

export default function ProfileDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  const {
    lists,
    categories,
  } = useStaticDataQueries();

  // Navigation Hook
  const profileId = parseInt(id || '0');
  const {
    previous,
    next,
    hasPrevious,
    hasNext,
    position,
    total,
    isLoading: isNavigationLoading
  } = useProfileNavigation(profileId);

  // Fetch Profile
  const { data: profile, isLoading, error, refetch } = useQuery({
    queryKey: ['profile', id],
    queryFn: () => ApiClient.get<ProfileWithRelations>(`/profiles/${id}`),
  });

  // Update Mutation
  const updateMutation = useMutation({
    mutationFn: (updates: Partial<ProfileWithRelations>) =>
      ApiClient.put(`/profiles/${id}`, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile', id] });
    },
  });

  // ✅ FIXED: Proper update handler with refetch
  const handleUpdate = async (updates: Partial<ProfileWithRelations>) => {
    try {
      if (Object.keys(updates).length > 0) {
        // Only call API if there are actual updates
        await updateMutation.mutateAsync(updates);
      } else {
        // Empty updates = just refetch (used by RelationsSection)
        await refetch();
        console.log('[ProfileDetail] Refetched profile data');
      }
    } catch (error) {
      console.error('[ProfileDetail] Update failed:', error);
      // TODO: Toast notification
    }
  };

  const handleBack = () => {
    navigate(`/profile-manager?${searchParams.toString()}`);
  };

  // Navigation Handlers
  const handlePrevious = () => {
    if (previous) {
      navigate(`/profile-manager/${previous}?${searchParams.toString()}`);
    }
  };

  const handleNext = () => {
    if (next) {
      navigate(`/profile-manager/${next}?${searchParams.toString()}`);
    }
  };

  if (isLoading) {
    return <div className="p-6">Loading profile...</div>;
  };

  if (error || !profile) {
    return <div className="p-6">Profile not found</div>;
  }

  return (
    <div className="container mx-auto p-6">
      {/* Header with Navigation */}
      <ProfileDetailHeader 
        profile={profile} 
        onBack={handleBack}
        onPrevious={handlePrevious}
        onNext={handleNext}
        hasPrevious={hasPrevious}
        hasNext={hasNext}
        position={position}
        total={total}
        isNavigationLoading={isNavigationLoading}
      />

      {/* Two Column Layout */}
      <div className="grid grid-cols-2 gap-6 mt-6">
        {/* Left Column: Editable Data */}
        <div className="space-y-6 overflow-y-auto">
          <BasicInformationSection profile={profile} onUpdate={handleUpdate} />
          <LocationSection profile={profile} onUpdate={handleUpdate} />
          <ProfileDataSection profile={profile} onUpdate={handleUpdate} />
          <RelationsSection profile={profile} onUpdate={handleUpdate} categories={categories} lists={lists} />
          <StatusMarkersSection profile={profile} onUpdate={handleUpdate} />
          <TechnicalDataSection profile={profile} />
        </div>

        {/* Right Column: Profile Image & Data */}
        <div className="sticky top-6">
          <ProfileImageAndData profile={profile} />
        </div>
      </div>
    </div>
  );
}